import type { SourceMaterialKind } from '@mira/clients-shared';

export const maxConversationSources = 3;
export const maxConfirmedSourceTextChars = 60_000;

export interface WebSourceContract {
  kind: SourceMaterialKind;
  maxBytes: number;
}

export const sourceMimeContracts: Readonly<Record<string, WebSourceContract>> = {
  'text/plain': { kind: 'file', maxBytes: 5 * 1024 * 1024 },
  'text/markdown': { kind: 'file', maxBytes: 5 * 1024 * 1024 },
  'text/csv': { kind: 'file', maxBytes: 5 * 1024 * 1024 },
  'application/json': { kind: 'file', maxBytes: 5 * 1024 * 1024 },
  'application/pdf': { kind: 'file', maxBytes: 10 * 1024 * 1024 },
  'image/png': { kind: 'image', maxBytes: 10 * 1024 * 1024 },
  'image/jpeg': { kind: 'image', maxBytes: 10 * 1024 * 1024 },
  'image/webp': { kind: 'image', maxBytes: 10 * 1024 * 1024 },
  'audio/webm': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/mp4': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/mpeg': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/wav': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/ogg': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
};

export class SourceMaterialInputError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = 'SourceMaterialInputError';
  }
}

export function normalizedSourceMimeType(mimeType: string): string {
  return mimeType.split(';', 1)[0]?.trim().toLowerCase() || '';
}

export function validateWebSourceFile(file: Pick<File, 'name' | 'size' | 'type'>, expectedKind?: SourceMaterialKind): WebSourceContract & { mimeType: string } {
  if (!file.name.trim() || file.name.trim().length > 180) {
    throw new SourceMaterialInputError('invalid_name', 'File name cannot be empty and must be at most 180 characters.');
  }
  const mimeType = normalizedSourceMimeType(file.type) || inferredTextMimeType(file.name);
  const contract = sourceMimeContracts[mimeType];
  if (!contract) throw new SourceMaterialInputError('unsupported_type', 'This file format is not supported yet.');
  if (expectedKind && contract.kind !== expectedKind) {
    throw new SourceMaterialInputError('kind_mismatch', expectedKind === 'image' ? 'Choose a PNG, JPEG, or WebP image.' : 'Choose a supported text file or PDF.');
  }
  if (!Number.isInteger(file.size) || file.size <= 0) throw new SourceMaterialInputError('empty_file', 'This file has no readable content.');
  if (file.size > contract.maxBytes) throw new SourceMaterialInputError('oversized_file', `Files cannot exceed ${formatBytes(contract.maxBytes)}.`);
  return { ...contract, mimeType };
}

export function validateConfirmedSourceText(text: string): string {
  const confirmed = text.trim();
  if (!confirmed) throw new SourceMaterialInputError('empty_text', 'No sendable text was recognized. Check it or pick another file.');
  if (confirmed.length > maxConfirmedSourceTextChars) {
    throw new SourceMaterialInputError('oversized_text', `Confirmed text cannot exceed ${maxConfirmedSourceTextChars.toLocaleString('en-US')} characters.`);
  }
  return confirmed;
}

function inferredTextMimeType(name: string): string {
  const lower = name.toLowerCase();
  if (lower.endsWith('.md') || lower.endsWith('.markdown')) return 'text/markdown';
  if (lower.endsWith('.csv')) return 'text/csv';
  if (lower.endsWith('.json')) return 'application/json';
  if (lower.endsWith('.txt')) return 'text/plain';
  return '';
}

function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${Math.round(bytes / (1024 * 1024))} MB` : `${Math.round(bytes / 1024)} KB`;
}
